import React, { useContext, useEffect, useState, useMemo }  from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity, Platform, Linking, Image, SafeAreaView } from 'react-native'
import { Button, Text, Spinner, ViewPager, Icon } from '@ui-kitten/components'
import Purchases from 'react-native-purchases'
import { UserContext } from '../../global/UserProvider'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { PRICING_PLANS } from '../../utils/pricingPlans'
import { useAtomValue, useAtom } from 'jotai'
import { organizationAtom, organizationSubscriptionInfoAtom, userLocaleAtom } from '../../global/AtomsProvider'
import { HintView } from '../../components/HintView'
import { useColors } from '../../utils/useColors'
import { syncOrganizationSubscription } from '../../global/Firebase/organizationSubscription'
import { applyRevenueCatIdentity } from '../../utils/revenueCatIdentity'

const APIKeys = {
  apple: "appl_jlaQJYaeoluKxjaoiTHZRxcTJrR",
  google: "your_revenuecat_google_api_key",
};

export default function PaywallScreen( { navigation, route } ) {
  const colors = useColors()
  const { t } = useTranslation()
  const { showCloseButton } = route.params || {}
  const [context, setContext] = useContext(UserContext)
  const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
  const [organization, setOrganization] = useAtom(organizationAtom)
  const userLocale = useAtomValue(userLocaleAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [paymentScriptLoaded, setPaymentScriptLoaded] = useState(false)
  const [localizedPricingPlans, setLocalizedPricingPlans] = useState(PRICING_PLANS.map(item => ({...item, text: t('pricingPlans.' + item.id + 'Name'), description: t('pricingPlans.' + item.id + 'Description')})))
  const [selectedPlan, setSelectedPlan] = useState(localizedPricingPlans[2])
  const [freePlanVisible, setFreePlanVisible] = useState(true)
  const [reviewIndex, setReviewIndex] = useState(0)
  const localizedReviews = useMemo(() => {
    return ['1', '2', '3', '4', '5'].map(item => ({id: item, name: t('reviews.reviewName' + item), review: t('reviews.reviewText' + item)}))
  }, [userLocale])

  useEffect(() => {
    navigation.setOptions({
      headerShown: showCloseButton ? true : false
    })
  }, [])

  useEffect(() => {
    if (showCloseButton) {
      if (organizationSubscriptionInfo?.subscriptionPlan !== 'free' && Platform.OS === 'ios') {
        navigation.navigate('Home')
      }
    } else {
      if (organizationSubscriptionInfo?.subscriptionPlan && Platform.OS === 'ios') {
        navigation.navigate('Home')
      }
    }
  }, [organizationSubscriptionInfo?.subscriptionPlan])

  useEffect(() => {
    if (Platform.OS === 'android') {
      // ATL-855: this effect used to write `subscriptionPlan: 'free'` UNCONDITIONALLY on every
      // mount. That is the ATL-760 downgrade shape, and mirroring it to Supabase would have hit 54
      // paying organizations. It now states an INTENT and the server decides: it refuses to
      // establish `free` while a live paid entitlement is on record, so a paying customer landing
      // here (e.g. via the /paywall deep link) keeps their plan.
      //
      // ATL-851: still routed through the helper, which resolves the org id in BOTH read modes —
      // `organization.ref` is a plain `{ id }` shim once an org is flipped. An effect cannot
      // await, so a failure is reported rather than left as an invisible unhandled rejection.
      syncOrganizationSubscription(organization)
        .then(result => {
          // Only reflect locally what the SERVER actually applied. Optimistically showing 'free'
          // after a refusal would tell a paying customer they had been downgraded.
          if (result?.plan) setOrganization({...organization, subscriptionPlan: result.plan})
        })
        .catch(error => Sentry.captureException(error))
      navigation.navigate('Home')
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setReviewIndex((prevIndex) => (prevIndex + 1) % localizedReviews.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [localizedReviews.length])

  useEffect(() => {
    let script
    if (Platform.OS === 'web') {
      script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/paddle.js'
      script.async = true;
      script.addEventListener('load', () => {
        const Paddle = window.Paddle;
        // Paddle.Environment.set('sandbox');
        Paddle.Setup({ 
          vendor: 159571,
          eventCallback: function(data) {
            if (data.event === 'Checkout.PaymentComplete') {
              // Check to ensure the payment has not been flagged for manual fraud review
              if (!data.eventData.flagged) {
                var checkoutId = data.eventData.checkout.id
                Paddle.Order.details(checkoutId, function(data) {
                  if (data) {
                    // Order data, downloads, receipts etc... available within 'data' variable
                    console.log(data)
                  } else {
                    // Order processing delay - order details cannot be retrieved at the moment
                    console.log('Order is being processed')
                  }
                  setOrganization({...organization, subscriptionPlan: selectedPlan?.id})
                })
                navigation.navigate('Home')
              } else {
                // Payment has not been fully processed at the moment, so order details cannot be retrieved
                console.log('Transaction pending review');
              }
            }
          }
        });
        setPaymentScriptLoaded(true)
      })
      document.body.appendChild(script);
    } else if (Platform.OS === 'ios') {
      // ASO-AN-001: the App User ID stays the ORGANIZATION id — an Atlas entitlement is owned by
      // the org, the backend webhook resolves the org from `app_user_id`, and the web plan view
      // reads /v1/subscribers/{organizationId}. What changed is only HOW that identity is applied:
      // a bare `configure` cannot switch the SDK to a different organization (that needs `logIn`),
      // so a device whose account moved orgs kept serving the previous org's customer info.
      // See utils/revenueCatIdentity.js.
      void applyRevenueCatIdentity({
        purchases: Purchases,
        apiKey: APIKeys.apple,
        organizationId: context.user.organizationId,
        attributes: { "userId" : context.user.uid, "organizationName" : organizationSubscriptionInfo?.name, "userEmail" : context.user.email },
      }).then(getRevenueCatOfferings)
    }

    return () => {
      if (Platform.OS === 'web') {
        document.body.removeChild(script)
      } else if (Platform.OS === 'ios') {
        Purchases.removeCustomerInfoUpdateListener()
      }
    }
  }, [])

  const getRevenueCatOfferings = async () => {
    try {
      let offerings = await Purchases.getOfferings()
      setLocalizedPricingPlans(localizedPricingPlans.map(plan => {
        let newProduct = offerings.all.freemium0.availablePackages.find(product => product.identifier === plan.revenuecatPackageId);
        return { ...plan, revenuecatPackage: newProduct };
      }))
      setSelectedPlan(selectedPlan => ({...selectedPlan, revenuecatPackage: offerings.all.freemium0.availablePackages.find(product => product.identifier === selectedPlan.revenuecatPackageId)}))
    } catch (e) {
      console.log('err', e)
    }
  }

  const submit = async (plan) => {
    if (plan.id === 'free') {
      // ATL-855: the server decides. It establishes `free` only while nothing paid is on record,
      // and writes BOTH stores itself — there is deliberately no local Firestore write left here,
      // because a second writer with a weaker guard is how ATL-760 happened.
      // ATL-851: this `return` sits BEFORE the only try/catch in submit(), so a throw here takes
      // the screen down; the helper resolves the org id in both read modes so it does not.
      const result = await syncOrganizationSubscription(organization)
      if (result?.plan) setOrganization({...organization, subscriptionPlan: result.plan})
      navigation.navigate('Home')
      return
    }
    if (Platform.OS === 'web') {
      const Paddle = window.Paddle
      Paddle.Checkout.open({ 
        product: plan.paddleId,
        title: plan.text,
        message: plan.description,
        locale: userLocale,
        email: context.user.email,
        passthrough: JSON.stringify({ organizationId: context.user.organizationId, userId: context.user.uid, orgName: organizationSubscriptionInfo?.name }),
      })
    } else if (Platform.OS === 'ios') {
      setIsLoading(true)
      let purchaseResult
      try {
        purchaseResult = await Purchases.purchasePackage(plan.revenuecatPackage)
        setOrganization({...organization, subscriptionPlan: plan.id})
      } catch (error) {
        console.log(error)
        if (error.code !== 1) Sentry.captureException(error)
      }
      if (purchaseResult?.customerInfo?.entitlements?.active?.length > 0) {
        navigation.navigate('Home')
      }
      setIsLoading(false)
    }
  }

  const restorePurchases = async () => {
    setIsLoading(true)
    try {
      const customerInfo = await Purchases.restorePurchases()
      // check if we know about this subscription
      if (customerInfo?.entitlements?.active?.length > 0 && organizationSubscriptionInfo.status !== 'active') {
        // ATL-855: the device's `customerInfo.entitlements.active[0].product_id` is NOT sent and
        // is NOT trusted — it is an unverified value read off the handset, and an endpoint that
        // accepted it would let any user grant their own org a paid plan. The backend re-derives
        // the entitlement from the RevenueCat/Paddle webhooks it has stored and applies that.
        //
        // The honest consequence: a purchase whose webhook never reached us cannot be restored
        // this way, and the user is told there is nothing to restore rather than being granted
        // something unverified. Closing that needs a backend-held RevenueCat key (see ATL-855).
        //
        // ATL-851: awaited, so a failure is caught by this function's try/catch instead of
        // navigating Home as if the plan had been recorded.
        // `plan` is what the org HOLDS after the call, which is the right thing to branch on: a
        // refusal ('paid-entitlement-on-record') reports the paid plan the org already had, and
        // that is a successful restore from the user's point of view even though nothing changed.
        const result = await syncOrganizationSubscription(organization)
        if (result?.plan && result.plan !== 'free') {
          setOrganization({...organization, subscriptionPlan: result.plan})
          navigation.navigate('Home')
        } else {
          alert(t('billing.noPurchases'))
        }
      } else {
        alert(t('billing.noPurchases'))
      }
    } catch (error) {
      console.log(error)
      if (error.code !== 1) Sentry.captureException(error)
    }
    setIsLoading(false)
  }

  return (
    <SafeAreaView style={{flex: 1}}>
      <ScrollView style={styles.container}>
        
        { Platform.OS === 'web' && organization?.currency?.id === 'rub' && (
          <HintView
            text={t('billing.webHintRussia')}
            iconName='alert-circle-outline' style={{marginTop: 10, marginBottom: 20}} 
            action={() => Linking.openURL('https://atlas-app.notion.site/c773e64af87240edb4b328ce89d83a22?pvs=4')}
            callToAction={t('openLink')}
          />
        )}

        { Platform.OS === 'ios' && organization?.currency?.id === 'rub' && (
          <HintView
            text={t('billing.iosHintRussia')}
            iconName='alert-circle-outline' style={{marginTop: 10, marginBottom: 20}} 
            action={() => Linking.openURL('https://atlas-app.notion.site/c773e64af87240edb4b328ce89d83a22?pvs=4')}
            callToAction={t('openLink')}
          />
        )}

        <Image resizeMode='contain' source={require('../../assets/platform.png')} style={styles.image}/>
      
        <Text category='h1' style={styles.label}>
          {t('paywall.title')}
        </Text>
        <Text category='h6' style={{marginBottom: 20}}>
          {t('paywall.subtitle')}
        </Text>

        <ViewPager style={styles.viewPager} selectedIndex={reviewIndex} onSelect={setReviewIndex} useNativeDriver={true}>
          { localizedReviews.map((review, index) => (
            <View key={review.id} style={styles.page}>
              <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 5 }}>
                {Array(5).fill().map((_, i) => (
                  <Icon key={i} name='star' width={20} height={20} fill='#FFD700' />
                ))}
              </View>
              <Text style={styles.reviewText} category='s2'>{review.review}</Text>
              <Text style={styles.reviewName} appearance='hint' category='c1'>{review.name}</Text>
            </View>
          ))}
        </ViewPager>
        <View style={{marginBottom: 20, flexDirection: 'row', justifyContent: 'center'}}>
          { localizedReviews.map((review, index) => (
            <TouchableOpacity style={{marginHorizontal: 5}} key={review.id} onPress={() => setReviewIndex(index)}>
              <Icon name={reviewIndex == index ? 'radio-button-on' : 'radio-button-off-outline'} width={15} height={15} fill={colors.mediumGrey} />
            </TouchableOpacity>
          ))}
        </View>

        <View>
          { localizedPricingPlans.map((plan, index) => (
            (freePlanVisible || plan.id !== 'free') && (
              <TouchableOpacity key={plan.id} onPress={() => {
                if (selectedPlan?.id === plan.id) {
                  submit(plan)
                }
                setSelectedPlan(plan)
              }} >
                <View style={selectedPlan?.id === plan.id ? [styles.pricingCard, styles.selectedPricingCard, { borderColor: colors.primary }] : styles.pricingCard}>
                  {plan.id === 'tier2' && <Text category='c2' style={{position: 'absolute', top: -11, right: 5, backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4, color: 'white', overflow: 'hidden'}}>{t('paywall.popular')}</Text>}
                  <Text category='h6' style={{marginVertical: 5}}>{plan.text}</Text>
                  <Text category='p1' style={{marginBottom: 5, textAlign: 'center'}}>{plan.description}</Text>
                  <Text category='s1' style={{marginBottom: 10}}>{(plan.revenuecatPackage ? plan.revenuecatPackage?.product?.priceString : ('$' + plan.price.usd)) + t('billing.perMonth')}</Text>
                </View>
              </TouchableOpacity>
            )
          ))}
        </View>
        
        {/* { !freePlanVisible && (
          <TouchableOpacity onPress={() => setFreePlanVisible(true)}>
            <Text appearance='hint' style={{marginBottom: 20, textAlign: 'center'}}>{t('paywall.seeOtherPlans')}</Text>
          </TouchableOpacity>
        )} */}
        
        { isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          <Button disabled={(Platform.OS === 'web' && !paymentScriptLoaded)} onPress={() => submit(selectedPlan)} style={styles.payButton}>{t('paywall.cta')}</Button>
        )}
        
        { Platform.OS === 'ios' && (
          <TouchableOpacity onPress={() => restorePurchases()}>
            <Text appearance='hint' style={{marginVertical: 20, textAlign: 'center'}}>{t('billing.restorePurchases')}</Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1, 
    padding: 20,
    // paddingTop: 100,
    minWidth: '100%',
    maxWidth: 800,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  // submitButton: {
  //   marginTop: 16,
  // },
  spinner: {
    alignItems: 'center'
  },
  pricingCard: {
    // flex: 1,
    backgroundColor: 'white',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center', 
    minWidth: 200,
    marginBottom: 20
  },
  selectedPricingCard: {
    borderWidth: 2,
  },
  payButton: {

  },
  viewPager: {
    // flex: 1,
    alignSelf: 'stretch',
    marginBottom: 10
  },
  page:{
    // flex: 1,
    alignItems: 'center',
    // marginTop: 50
    justifyContent: 'center',
    maxWidth: 600,
    alignSelf: 'center'
  },
  reviewText: {
    marginBottom: 5,
    paddingHorizontal: 20,
    textAlign: 'center'
  },
  reviewName: {
    paddingHorizontal: 20,
    textAlign: 'center'
  },
  image: {
    maxWidth: '100%',
    alignSelf: 'center',
    maxHeight: '20%'
  }
});